import React, { useEffect, useMemo, useState } from 'react';
import { UserCog, IdCard, ShieldCheck, Sparkles, Palette, CalendarRange } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { SelectField, CheckboxGroup } from './ui/select-field';
import * as OPT from '../lib/operatingModelOptions';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { workspaceAdminAPI } from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const joinList = (value) => (Array.isArray(value) ? value.join(', ') : '');
const parseList = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const TenantOperatingModelPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0);
  const [form, setForm] = useState(null);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await workspaceAdminAPI.getWorkspaceOperatingModel();
      setForm(response?.data?.config_value || {});
      setVersion(response?.data?.version || 0);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load workspace operating model'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const updateSectionValue = (section, key, value) => {
    setForm((prev) => ({
      ...(prev || {}),
      [section]: {
        ...(prev?.[section] || {}),
        [key]: value,
      },
    }));
  };

  const updateNestedValue = (section, group, key, value) => {
    setForm((prev) => ({
      ...(prev || {}),
      [section]: {
        ...(prev?.[section] || {}),
        [group]: {
          ...(prev?.[section]?.[group] || {}),
          [key]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await workspaceAdminAPI.updateWorkspaceOperatingModel({ config_value: form || {} });
      setForm(response?.data?.config_value || {});
      setVersion(response?.data?.version || 0);
      toast.success('Workspace operating model updated');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update workspace operating model'));
    } finally {
      setSaving(false);
    }
  };

  const summary = useMemo(() => {
    if (!form) return [];
    return [
      {
        label: 'Invites',
        value: form.user_lifecycle_operations?.invite_enabled ? 'Enabled' : 'Disabled',
      },
      {
        label: 'Approval Levels',
        value: form.access_assignment_boundaries?.approval_hierarchies?.leave_levels ?? 0,
      },
      {
        label: 'Brand Locale',
        value: form.tenant_branding_communications?.localized_messaging?.default_language || 'en',
      },
      {
        label: 'Payroll Cycle',
        value: form.local_compliance_configuration?.payroll_settings?.pay_cycle || 'monthly',
      },
    ];
  }, [form]);

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading workspace operating model...</div>;
  }

  if (!form) {
    return <div className="text-sm text-muted-foreground">Workspace operating model unavailable.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-xl font-semibold">Tenant Operating Model</h3>
          <p className="text-sm text-muted-foreground">
            Tenant-owned configuration surface for workforce operations, experience, branding, and local compliance. Version {version || 0}.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadProfile}>
            Refresh
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Operating Model'}
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        {summary.map((item) => (
          <Card key={item.label}>
            <CardContent className="pt-5">
              <p className="text-xs text-muted-foreground">{item.label}</p>
              <p className="mt-2 text-2xl font-semibold">{item.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              User Lifecycle Operations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label>Invite Employees</Label>
                  <p className="text-xs text-muted-foreground">Tenant controls who can invite new workforce users.</p>
                </div>
                <Switch
                  checked={Boolean(form.user_lifecycle_operations?.invite_enabled)}
                  onCheckedChange={(checked) => updateSectionValue('user_lifecycle_operations', 'invite_enabled', checked)}
                />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label>Suspend Users</Label>
                  <p className="text-xs text-muted-foreground">Allows tenant administrators to freeze local access.</p>
                </div>
                <Switch
                  checked={Boolean(form.user_lifecycle_operations?.suspension_enabled)}
                  onCheckedChange={(checked) => updateSectionValue('user_lifecycle_operations', 'suspension_enabled', checked)}
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Manager Assignment Mode</Label>
                <SelectField
                  value={form.user_lifecycle_operations?.manager_assignment_mode || ''}
                  onChange={(next) => updateSectionValue('user_lifecycle_operations', 'manager_assignment_mode', next)}
                  options={OPT.MANAGER_ASSIGNMENT_MODES}
                  />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label>Require Offboarding Checklist</Label>
                  <p className="text-xs text-muted-foreground">Keeps separations aligned with tenant process.</p>
                </div>
                <Switch
                  checked={Boolean(form.user_lifecycle_operations?.offboarding_checklist_required)}
                  onCheckedChange={(checked) => updateSectionValue('user_lifecycle_operations', 'offboarding_checklist_required', checked)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <IdCard className="h-5 w-5" />
              End-User Profile & Org Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Employee Fields</Label>
              <Textarea
                value={joinList(form.end_user_profile_org_settings?.employee_fields)}
                onChange={(e) => updateSectionValue('end_user_profile_org_settings', 'employee_fields', parseList(e.target.value))}
                placeholder="department, designation, location, cost_center"
              />
            </div>
            <div>
              <Label>Custom Attributes</Label>
              <Textarea
                value={joinList(form.end_user_profile_org_settings?.custom_attributes)}
                onChange={(e) => updateSectionValue('end_user_profile_org_settings', 'custom_attributes', parseList(e.target.value))}
                placeholder="shirt_size, emergency_level, badge_zone"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Org Structure Mapping</Label>
                <SelectField
                  value={form.end_user_profile_org_settings?.org_structure_mapping_mode || ''}
                  onChange={(next) => updateSectionValue('end_user_profile_org_settings', 'org_structure_mapping_mode', next)}
                  options={OPT.ORG_STRUCTURE_MODES}
                  />
              </div>
              <div>
                <Label>Designation Rule Mode</Label>
                <SelectField
                  value={form.end_user_profile_org_settings?.designation_rule_mode || ''}
                  onChange={(next) => updateSectionValue('end_user_profile_org_settings', 'designation_rule_mode', next)}
                  options={OPT.DESIGNATION_RULE_MODES}
                  />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Access Assignment Within Allowed Boundaries
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label>Allow Role Assignment</Label>
                <p className="text-xs text-muted-foreground">Tenant can assign local roles without changing platform baselines.</p>
              </div>
              <Switch
                checked={Boolean(form.access_assignment_boundaries?.allow_role_assignment)}
                onCheckedChange={(checked) => updateSectionValue('access_assignment_boundaries', 'allow_role_assignment', checked)}
              />
            </div>
            <div>
              <Label>Local Access Groups</Label>
              <Textarea
                value={joinList(form.access_assignment_boundaries?.local_access_groups)}
                onChange={(e) => updateSectionValue('access_assignment_boundaries', 'local_access_groups', parseList(e.target.value))}
                placeholder="finance_approvers, shift_leads, onboarding_owners"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label>Leave Approval Levels</Label>
                <SelectField
                  value={form.access_assignment_boundaries?.approval_hierarchies?.leave_levels ?? 2}
                  onChange={(next) => updateNestedValue('access_assignment_boundaries', 'approval_hierarchies', 'leave_levels', Number(next || 0))}
                  options={OPT.APPROVAL_LEVELS}
                  />
              </div>
              <div>
                <Label>Expense Approval Levels</Label>
                <SelectField
                  value={form.access_assignment_boundaries?.approval_hierarchies?.expense_levels ?? 2}
                  onChange={(next) => updateNestedValue('access_assignment_boundaries', 'approval_hierarchies', 'expense_levels', Number(next || 0))}
                  options={OPT.APPROVAL_LEVELS}
                  />
              </div>
              <div>
                <Label>Team Permissions Model</Label>
                <SelectField
                  value={form.access_assignment_boundaries?.team_permissions_model || ''}
                  onChange={(next) => updateSectionValue('access_assignment_boundaries', 'team_permissions_model', next)}
                  options={OPT.TEAM_PERMISSION_MODELS}
                  />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Employee Experience Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Leave Rules Visibility</Label>
                <SelectField
                  value={form.employee_experience_settings?.leave_rules_visibility || ''}
                  onChange={(next) => updateSectionValue('employee_experience_settings', 'leave_rules_visibility', next)}
                  options={OPT.LEAVE_RULE_VISIBILITY}
                  />
              </div>
              <div>
                <Label>Onboarding Journeys</Label>
                <CheckboxGroup                  value={form.employee_experience_settings?.onboarding_journeys || []}
                  onChange={(next) => updateSectionValue('employee_experience_settings', 'onboarding_journeys', next)}
                  options={OPT.ONBOARDING_JOURNEYS}
                />
              </div>
            </div>
            <div>
              <Label>Helpdesk Categories</Label>
              <Textarea
                value={joinList(form.employee_experience_settings?.helpdesk_categories)}
                onChange={(e) => updateSectionValue('employee_experience_settings', 'helpdesk_categories', parseList(e.target.value))}
                placeholder="IT, HR, Payroll, Admin"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label>Self-Service Enabled</Label>
                  <p className="text-xs text-muted-foreground">Employee self-service remains tenant configurable.</p>
                </div>
                <Switch
                  checked={Boolean(form.employee_experience_settings?.self_service_options?.profile_edit_requests)}
                  onCheckedChange={(checked) => updateNestedValue('employee_experience_settings', 'self_service_options', 'profile_edit_requests', checked)}
                />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label>Internal Notifications</Label>
                  <p className="text-xs text-muted-foreground">Controls employee-facing internal announcements.</p>
                </div>
                <Switch
                  checked={Boolean(form.employee_experience_settings?.internal_notifications_enabled)}
                  onCheckedChange={(checked) => updateSectionValue('employee_experience_settings', 'internal_notifications_enabled', checked)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Tenant Branding & Communications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Brand Name</Label>
                <Input
                  value={form.tenant_branding_communications?.portal_branding?.brand_name || ''}
                  onChange={(e) => updateNestedValue('tenant_branding_communications', 'portal_branding', 'brand_name', e.target.value)}
                />
              </div>
              <div>
                <Label>Primary Color</Label>
                {/* Swatch beside the hex, both bound to the same value: typing
                    `#0F766E` and picking it should be the same act. */}
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="Primary colour"
                    className="h-10 w-12 rounded-md border border-input bg-background"
                    value={form.tenant_branding_communications?.portal_branding?.primary_color || '#3b4fb8'}
                    onChange={(e) => updateNestedValue('tenant_branding_communications', 'portal_branding', 'primary_color', e.target.value)}
                  />
                  <Input
                    className="font-mono"
                    value={form.tenant_branding_communications?.portal_branding?.primary_color || '#3b4fb8'}
                    onChange={(e) => updateNestedValue('tenant_branding_communications', 'portal_branding', 'primary_color', e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Welcome Email Variant</Label>
                <SelectField
                  value={form.tenant_branding_communications?.email_content_variants?.welcome_email_variant || ''}
                  onChange={(next) => updateNestedValue('tenant_branding_communications', 'email_content_variants', 'welcome_email_variant', next)}
                  options={OPT.WELCOME_EMAIL_VARIANTS}
                  />
              </div>
              <div>
                <Label>Employee-Facing Templates</Label>
                <Input
                  value={joinList(form.tenant_branding_communications?.employee_facing_templates)}
                  onChange={(e) => updateSectionValue('tenant_branding_communications', 'employee_facing_templates', parseList(e.target.value))}
                  placeholder="welcome, leave_approved, helpdesk_update"
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Default Language</Label>
                <SelectField
                  value={form.tenant_branding_communications?.localized_messaging?.default_language || ''}
                  onChange={(next) => updateNestedValue('tenant_branding_communications', 'localized_messaging', 'default_language', next)}
                  options={OPT.LANGUAGES}
                  />
              </div>
              <div>
                <Label>Fallback Language</Label>
                <SelectField
                  value={form.tenant_branding_communications?.localized_messaging?.fallback_language || ''}
                  onChange={(next) => updateNestedValue('tenant_branding_communications', 'localized_messaging', 'fallback_language', next)}
                  options={OPT.LANGUAGES}
                  />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarRange className="h-5 w-5" />
              Local Compliance Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Default Region</Label>
                <SelectField
                  value={form.local_compliance_configuration?.regional_holidays?.default_region || ''}
                  onChange={(next) => updateNestedValue('local_compliance_configuration', 'regional_holidays', 'default_region', next)}
                  options={OPT.REGIONS}
                  />
              </div>
              <div>
                <Label>Calendar Name</Label>
                <SelectField
                  value={form.local_compliance_configuration?.working_calendars?.calendar_name || ''}
                  onChange={(next) => updateNestedValue('local_compliance_configuration', 'working_calendars', 'calendar_name', next)}
                  options={OPT.WORKING_CALENDARS}
                  />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label>Weekly Off Days</Label>
                <CheckboxGroup                  value={form.local_compliance_configuration?.working_calendars?.weekly_off_days || []}
                  onChange={(next) => updateNestedValue('local_compliance_configuration', 'working_calendars', 'weekly_off_days', next)}
                  options={OPT.WEEKDAYS}
                />
              </div>
              <div>
                <Label>Payroll Cycle</Label>
                <SelectField
                  value={form.local_compliance_configuration?.payroll_settings?.pay_cycle || ''}
                  onChange={(next) => updateNestedValue('local_compliance_configuration', 'payroll_settings', 'pay_cycle', next)}
                  options={OPT.PAY_CYCLES}
                  />
              </div>
              <div>
                <Label>Compliance Profile</Label>
                <SelectField
                  value={form.local_compliance_configuration?.statutory_mappings?.compliance_profile || ''}
                  onChange={(next) => updateNestedValue('local_compliance_configuration', 'statutory_mappings', 'compliance_profile', next)}
                  options={OPT.COMPLIANCE_PROFILES}
                  />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Tax Regime Default</Label>
                <SelectField
                  value={form.local_compliance_configuration?.statutory_mappings?.tax_regime_default || ''}
                  onChange={(next) => updateNestedValue('local_compliance_configuration', 'statutory_mappings', 'tax_regime_default', next)}
                  options={OPT.TAX_REGIMES}
                  />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label>Require HR Review For Documents</Label>
                  <p className="text-xs text-muted-foreground">Supports local document workflow rules by entity or region.</p>
                </div>
                <Switch
                  checked={Boolean(form.local_compliance_configuration?.document_workflows?.require_hr_review)}
                  onCheckedChange={(checked) => updateNestedValue('local_compliance_configuration', 'document_workflows', 'require_hr_review', checked)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TenantOperatingModelPanel;
